<template>
  <div v-if="isLoading" class="loading-overlay">
    <div class="spinner-border text-primary" role="status">
      <span class="visually-hidden">Cargando...</span>
    </div>
  </div>

  <div class="row">
    <!-- Formulario de Usuario -->
    <div class="col-xl-7 d-flex">
      <div class="card flex-fill">
        <div class="card-header">
          <h5 class="card-title">
            <i class="fas fa-user-plus me-2"></i>
            {{ isEditing ? 'Editar Usuario' : 'Nuevo Usuario' }}
          </h5>
        </div>
        <div class="card-body card-buttons">
          <b-form action="javascript:;">
            <div class="row">
              <!-- Nombres -->
              <div class="col-md-6 mb-3">
                <label class="form-label">Nombres <span class="text-danger">*</span></label>
                <b-form-input
                  v-model="usuario.nombres"
                  type="text"
                  class="form-control"
                  :class="{ 'is-invalid': !isNombresValido && nombresTouched }"
                  placeholder="Ingresa los nombres"
                  required
                  @blur="nombresTouched = true"
                />
                <div v-if="!isNombresValido && nombresTouched" class="invalid-feedback">
                  Los nombres son requeridos
                </div>
              </div>

              <!-- Apellidos -->
              <div class="col-md-6 mb-3">
                <label class="form-label">Apellidos <span class="text-danger">*</span></label>
                <b-form-input
                  v-model="usuario.apellidos"
                  type="text"
                  class="form-control"
                  :class="{ 'is-invalid': !isApellidosValido && apellidosTouched }"
                  placeholder="Ingresa los apellidos"
                  required
                  @blur="apellidosTouched = true"
                />
                <div v-if="!isApellidosValido && apellidosTouched" class="invalid-feedback">
                  Los apellidos son requeridos
                </div>
              </div>

              <!-- Email -->
              <div class="col-md-6 mb-3">
                <label class="form-label">Email <span class="text-danger">*</span></label>
                <b-form-input
                  v-model="usuario.email"
                  type="email"
                  class="form-control"
                  :class="{ 'is-invalid': !isEmailValido && emailTouched }"
                  placeholder="correo@ejemplo.com"
                  required
                  @blur="emailTouched = true"
                />
                <div v-if="!isEmailValido && emailTouched" class="invalid-feedback">
                  Ingresa un email válido
                </div>
              </div>

              <!-- Rol -->
              <div class="col-md-6 mb-3">
                <label class="form-label">Rol <span class="text-danger">*</span></label>
                <select
                  v-model="usuario.rol"
                  class="form-select"
                  :class="{ 'is-invalid': !isRolValido && rolTouched }"
                  @blur="rolTouched = true"
                >
                  <option value="" disabled>Selecciona un rol</option>
                  <option value="Administrador">Administrador</option>
                  <option value="Supervisor">Supervisor</option>
                  <option value="Vendedor">Vendedor</option>
                </select>
                <div v-if="!isRolValido && rolTouched" class="invalid-feedback">
                  Selecciona un rol
                </div>
              </div>

              <!-- Password (solo para crear) -->
              <div v-if="!isEditing" class="col-md-6 mb-3">
                <label class="form-label">Contraseña <span class="text-danger">*</span></label>
                <b-form-input
                  v-model="usuario.password"
                  type="password"
                  class="form-control"
                  :class="{ 'is-invalid': !isPasswordValido && passwordTouched }"
                  placeholder="Mínimo 6 caracteres"
                  required
                  @blur="passwordTouched = true"
                />
                <div v-if="!isPasswordValido && passwordTouched" class="invalid-feedback">
                  La contraseña debe tener al menos 6 caracteres
                </div>
              </div>

              <!-- Estado -->
              <div class="col-md-6 mb-3">
                <div class="active-switch">
                  <label class="form-label">Estado</label>
                  <div class="status-toggle">
                    <input
                      id="estado"
                      class="check"
                      type="checkbox"
                      v-model="usuario.estado"
                    />
                    <label for="estado" class="checktoggle checkbox-bg"></label>
                  </div>
                  <small class="form-text text-muted d-block mt-2">
                    {{ usuario.estado ? 'Usuario activo' : 'Usuario inactivo' }}
                  </small>
                </div>
              </div>
            </div>

            <!-- Botones -->
            <div class="text-end mt-3">
              <b-button
                v-if="!isEditing"
                @click="submitForm"
                class="btn-primary m-1"
                :disabled="!isFormValid || isLoading"
              >
                <i class="fas fa-save me-2"></i>Guardar
              </b-button>
              <b-button
                v-if="isEditing"
                @click="updateUsuario"
                :disabled="!isFormValidEdit || isLoading"
                class="btn-edit m-1"
              >
                <i class="fas fa-edit me-2"></i>Actualizar
              </b-button>
              <b-button
                v-if="isEditing"
                @click="cambiarEstado"
                class="btn-warning m-1"
              >
                <i class="fas fa-power-off me-2"></i>
                {{ usuario.estado ? 'Desactivar' : 'Activar' }}
              </b-button>
              <b-button @click="resetForm" class="btn-clean m-1">
                <i class="fas fa-broom me-2"></i>Limpiar
              </b-button>
            </div>
          </b-form>
        </div>
      </div>
    </div>

    <!-- Lista de Usuarios -->
    <div class="col-xl-5 d-flex">
      <div class="card super-admin-dash-card flex-fill">
        <div class="card-header">
          <div class="row align-items-center">
            <div class="col">
              <h5 class="card-title mb-0">
                <i class="fas fa-users me-2"></i>
                Lista de Usuarios ({{ usuarios.length }})
              </h5>
            </div>
          </div>
        </div>
        <div class="card-body p-0">
          <div class="table-responsive">
            <table class="table table-hover mb-0">
              <tbody>
                <tr v-if="usuarios.length === 0">
                  <td class="text-center py-4">
                    <i class="fas fa-users fa-3x text-muted mb-3 d-block"></i>
                    <p class="text-muted mb-0">No hay usuarios registrados</p>
                  </td>
                </tr>
                <tr
                  v-for="user in usuarios"
                  :key="user.id"
                  @click="selectUsuario(user)"
                  class="cursor-pointer"
                  :class="{ 'table-active': selectedUsuarioId === user.id }"
                >
                  <td>
                    <div class="d-flex align-items-center">
                      <div class="avatar-circle me-3">
                        <i class="fas fa-user"></i>
                      </div>
                      <div>
                        <h6 class="mb-0">{{ user.nombres }} {{ user.apellidos }}</h6>
                        <small class="text-muted">{{ user.email }}</small>
                        <div class="mt-1">
                          <span class="badge" :class="getRolBadgeClass(user.rol)">
                            {{ user.rol }}
                          </span>
                          <span
                            class="badge ms-1"
                            :class="user.estado ? 'bg-success' : 'bg-danger'"
                          >
                            {{ user.estado ? 'Activo' : 'Inactivo' }}
                          </span>
                        </div>
                      </div>
                    </div>
                  </td>
                </tr>
              </tbody>
            </table>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import api from '@/api';

export default {
  name: 'Usuario',

  data() {
    return {
      usuarios: [],
      usuario: {
        nombres: '',
        apellidos: '',
        email: '',
        rol: '',
        password: '',
        estado: true
      },
      isLoading: false,
      error: null,
      isEditing: false,
      selectedUsuarioId: null,
      // Validaciones
      nombresTouched: false,
      apellidosTouched: false,
      emailTouched: false,
      rolTouched: false,
      passwordTouched: false
    };
  },

  mounted() {
    this.loadUsuarios();
  },

  computed: {
    isNombresValido() {
      return this.usuario.nombres.trim().length > 0;
    },
    isApellidosValido() {
      return this.usuario.apellidos.trim().length > 0;
    },
    isEmailValido() {
      const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
      return emailRegex.test(this.usuario.email);
    },
    isRolValido() {
      return this.usuario.rol !== '';
    },
    isPasswordValido() {
      return this.isEditing || this.usuario.password.length >= 6;
    },
    isFormValid() {
      return (
        this.isNombresValido &&
        this.isApellidosValido &&
        this.isEmailValido &&
        this.isRolValido &&
        this.isPasswordValido
      );
    },
    isFormValidEdit() {
      return (
        this.isNombresValido &&
        this.isApellidosValido &&
        this.isEmailValido &&
        this.isRolValido
      );
    }
  },

  methods: {
    async loadUsuarios() {
      try {
        this.isLoading = true;
        const response = await api.get('/usuario/');
        this.usuarios = response.data;
      } catch (err) {
        this.error = 'Error al cargar usuarios';
        console.error(err);
        this.$swal.fire({
          title: 'Error',
          text: 'No se pudieron cargar los usuarios',
          icon: 'error',
          confirmButtonText: 'OK'
        });
      } finally {
        this.isLoading = false;
      }
    },

    async submitForm() {
      const result = await this.$swal.fire({
        title: '¿Guardar usuario?',
        text: 'Se creará un nuevo usuario.',
        icon: 'question',
        showCancelButton: true,
        confirmButtonText: 'Sí, guardar',
        cancelButtonText: 'Cancelar'
      });

      if (!result.isConfirmed) return;

      this.isLoading = true;

      try {
        await api.post('/usuario', {
          nombres: this.usuario.nombres,
          apellidos: this.usuario.apellidos,
          email: this.usuario.email,
          rol: this.usuario.rol,
          password: this.usuario.password,
          estado: this.usuario.estado
        });

        this.$swal.fire({
          title: '¡Éxito!',
          text: 'Usuario creado correctamente',
          icon: 'success',
          timer: 2000,
          showConfirmButton: false
        });

        this.resetForm();
        this.loadUsuarios();
      } catch (err) {
        console.error(err);
        this.$swal.fire({
          title: 'Error',
          text: err.response?.data?.message || 'No se pudo crear el usuario',
          icon: 'error',
          confirmButtonText: 'OK'
        });
      } finally {
        this.isLoading = false;
      }
    },

    async updateUsuario() {
      const result = await this.$swal.fire({
        title: '¿Actualizar usuario?',
        text: 'Se actualizará la información del usuario.',
        icon: 'question',
        showCancelButton: true,
        confirmButtonText: 'Sí, actualizar',
        cancelButtonText: 'Cancelar'
      });

      if (!result.isConfirmed) return;

      this.isLoading = true;

      try {
        await api.put(`/usuario/${this.selectedUsuarioId}`, {
          nombres: this.usuario.nombres,
          apellidos: this.usuario.apellidos,
          email: this.usuario.email,
          rol: this.usuario.rol
        });

        this.$swal.fire({
          title: '¡Éxito!',
          text: 'Usuario actualizado correctamente',
          icon: 'success',
          timer: 2000,
          showConfirmButton: false
        });

        this.resetForm();
        this.loadUsuarios();
      } catch (err) {
        console.error(err);
        this.$swal.fire({
          title: 'Error',
          text: err.response?.data?.message || 'No se pudo actualizar el usuario',
          icon: 'error',
          confirmButtonText: 'OK'
        });
      } finally {
        this.isLoading = false;
      }
    },

    async cambiarEstado() {
      const nuevoEstado = !this.usuario.estado;
      const accion = nuevoEstado ? 'activar' : 'desactivar';

      const result = await this.$swal.fire({
        title: `¿${accion.charAt(0).toUpperCase() + accion.slice(1)} usuario?`,
        text: `El usuario será ${accion}do.`,
        icon: 'warning',
        showCancelButton: true,
        confirmButtonText: `Sí, ${accion}`,
        cancelButtonText: 'Cancelar'
      });

      if (!result.isConfirmed) return;

      this.isLoading = true;

      try {
        await api.put(`/usuario/cambiar_estado/${this.selectedUsuarioId}`, {
          estado: nuevoEstado
        });

        this.$swal.fire({
          title: '¡Éxito!',
          text: `Usuario ${accion}do correctamente`,
          icon: 'success',
          timer: 2000,
          showConfirmButton: false
        });

        this.resetForm();
        this.loadUsuarios();
      } catch (err) {
        console.error(err);
        this.$swal.fire({
          title: 'Error',
          text: err.response?.data?.message || `No se pudo ${accion} el usuario`,
          icon: 'error',
          confirmButtonText: 'OK'
        });
      } finally {
        this.isLoading = false;
      }
    },

    selectUsuario(user) {
      this.isEditing = true;
      this.selectedUsuarioId = user.id;
      this.usuario = {
        nombres: user.nombres,
        apellidos: user.apellidos,
        email: user.email,
        rol: user.rol,
        password: '', // No se muestra la contraseña
        estado: user.estado
      };
      // Reset touched states
      this.nombresTouched = false;
      this.apellidosTouched = false;
      this.emailTouched = false;
      this.rolTouched = false;
      this.passwordTouched = false;
    },

    resetForm() {
      this.usuario = {
        nombres: '',
        apellidos: '',
        email: '',
        rol: '',
        password: '',
        estado: true
      };
      this.isEditing = false;
      this.selectedUsuarioId = null;
      // Reset touched states
      this.nombresTouched = false;
      this.apellidosTouched = false;
      this.emailTouched = false;
      this.rolTouched = false;
      this.passwordTouched = false;
    },

    getRolBadgeClass(rol) {
      const classes = {
        'Administrador': 'bg-danger',
        'Supervisor': 'bg-warning text-dark',
        'Vendedor': 'bg-info'
      };
      return classes[rol] || 'bg-secondary';
    }
  }
};
</script>

<style scoped>
.loading-overlay {
  position: fixed;
  top: 0;
  left: 0;
  z-index: 1050;
  width: 100vw;
  height: 100vh;
  background-color: rgba(255, 255, 255, 0.8);
  display: flex;
  align-items: center;
  justify-content: center;
  backdrop-filter: blur(2px);
}

.is-invalid {
  border-color: #dc3545;
}

.invalid-feedback {
  display: block;
}

.cursor-pointer {
  cursor: pointer;
  transition: background-color 0.2s ease;
}

.cursor-pointer:hover {
  background-color: #f8f9fa;
}

.table-active {
  background-color: #e7f3ff !important;
}

.avatar-circle {
  width: 45px;
  height: 45px;
  border-radius: 50%;
  background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
  display: flex;
  align-items: center;
  justify-content: center;
  color: white;
  font-size: 1.2rem;
}

.badge {
  font-weight: 500;
  padding: 0.35em 0.65em;
}

.card {
  border: none;
  box-shadow: 0 0 20px rgba(0, 0, 0, 0.08);
}

.card-header {
  background-color: #fff;
  border-bottom: 1px solid #e9ecef;
  padding: 1rem 1.5rem;
}

.card-title {
  font-weight: 600;
  color: #2c3e50;
}

.btn-primary {
  background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
  border: none;
  font-weight: 600;
}

.btn-primary:hover:not(:disabled) {
  background: linear-gradient(135deg, #764ba2 0%, #667eea 100%);
  transform: translateY(-2px);
  box-shadow: 0 4px 12px rgba(102, 126, 234, 0.4);
}

.btn-edit {
  background-color: #28a745;
  color: white;
  border: none;
  font-weight: 600;
}

.btn-edit:hover:not(:disabled) {
  background-color: #218838;
  transform: translateY(-2px);
}

.btn-clean {
  background-color: #6c757d;
  color: white;
  border: none;
  font-weight: 600;
}

.btn-clean:hover {
  background-color: #5a6268;
  transform: translateY(-2px);
}

.btn-warning {
  font-weight: 600;
}

.table tbody tr {
  transition: background-color 0.2s ease;
}

.form-label {
  font-weight: 600;
  color: #495057;
  margin-bottom: 0.5rem;
}
</style>
